import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BtnPrimary, BtnSecondary, BtnDestructive } from "@/pages/Senders";
import { Plus, Trash, CheckCircle, Warning } from "@phosphor-icons/react";

const money = (n) => `$${Number(n || 0).toFixed(n < 0.01 ? 6 : 4)}`;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
// Retry a GET a few times with backoff — the backend can briefly time out
// while a large campaign is live, which otherwise blanks the models table.
async function getWithRetry(url, tries = 3) {
  let lastErr;
  for (let i = 0; i < tries; i++) {
    try { return await api.get(url); }
    catch (e) { lastErr = e; if (i < tries - 1) await sleep(700 * (i + 1)); }
  }
  throw lastErr;
}

export default function AiSettingsPanel() {
  const [providers, setProviders] = useState([]);
  const [models, setModels] = useState([]);
  const [cost, setCost] = useState(null);
  const [errLog, setErrLog] = useState(null);
  const [errLoading, setErrLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modelsFailed, setModelsFailed] = useState(false);

  const loadErrors = async () => {
    setErrLoading(true);
    try {
      const e = await api.get("/ai/errors?limit=30");
      setErrLog(e.data);
    } catch {
      toast.error("Couldn't load error log (server busy). Try again in a moment.");
    } finally { setErrLoading(false); }
  };
  const [fc, setFc] = useState({ findclix_api_key: "", findclix_base_url: "https://ai.findclix.com", findclix_enabled: true, configured: false });
  const [ds, setDs] = useState({ deepseek_api_key: "", deepseek_base_url: "https://api.deepseek.com", deepseek_model: "deepseek-v4-flash", configured: false });
  const [oa, setOa] = useState({ openai_api_key: "", openai_base_url: "https://api.openai.com", openai_enabled: true, configured: false });
  const [premiumFallback, setPremiumFallback] = useState("deepseek_direct_flash");
  const [testing, setTesting] = useState("");
  const [newModel, setNewModel] = useState({ key: "", provider: "findclix", model_id: "", display_name: "", role: "Custom" });

  const load = async () => {
    // Resilient load: the critical registry reads (providers + models) are
    // retried with backoff so a busy backend during a live campaign never
    // blanks the weights table. Cost/settings are best-effort.
    setLoading(true);
    const results = await Promise.allSettled([
      getWithRetry("/ai/providers"), getWithRetry("/ai/models"),
      api.get("/ai/cost/summary"), api.get("/settings"),
    ]);
    const [p, m, c, s] = results;
    if (p.status === "fulfilled") setProviders(p.value.data.providers);
    setModelsFailed(m.status !== "fulfilled");
    if (m.status === "fulfilled") setModels(m.value.data.models);
    if (c.status === "fulfilled") setCost(c.value.data);
    if (s.status === "fulfilled") {
      const sd = s.value.data;
      setFc({
        findclix_api_key: "",
        findclix_base_url: sd.findclix_base_url || "https://ai.findclix.com",
        findclix_enabled: sd.findclix_enabled ?? true,
        configured: sd.findclix_api_key_configured,
      });
      setDs({
        deepseek_api_key: "",
        deepseek_base_url: sd.deepseek_base_url || "https://api.deepseek.com",
        deepseek_model: sd.deepseek_model || "deepseek-v4-flash",
        configured: sd.deepseek_api_key_configured,
      });
      setOa({
        openai_api_key: "",
        openai_base_url: sd.openai_base_url || "https://api.openai.com",
        openai_enabled: sd.openai_enabled ?? true,
        configured: sd.openai_api_key_configured,
      });
      setPremiumFallback(sd.ai_premium_fallback || "deepseek_direct_flash");
    }
    setLoading(false);
    if (m.status !== "fulfilled") {
      toast.error("Couldn't load AI models (server busy). Tap Refresh in a moment.");
    } else if (results.some((r) => r.status === "rejected")) {
      toast.message("Some secondary AI stats loaded slowly — models & weights are up to date.");
    }
  };
  useEffect(() => { load(); }, []);

  const saveFindclix = async () => {
    try {
      const payload = { findclix_base_url: fc.findclix_base_url, findclix_enabled: fc.findclix_enabled };
      if (fc.findclix_api_key) payload.findclix_api_key = fc.findclix_api_key;
      await api.patch("/settings", payload);
      toast.success("FindClix settings saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const saveDeepseek = async () => {
    try {
      const payload = { deepseek_base_url: ds.deepseek_base_url, deepseek_model: ds.deepseek_model };
      if (ds.deepseek_api_key) payload.deepseek_api_key = ds.deepseek_api_key;
      await api.patch("/settings", payload);
      toast.success("DeepSeek Direct settings saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const saveOpenai = async () => {
    try {
      const payload = { openai_base_url: oa.openai_base_url, openai_enabled: oa.openai_enabled };
      if (oa.openai_api_key) payload.openai_api_key = oa.openai_api_key;
      await api.patch("/settings", payload);
      toast.success("OpenAI settings saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const testProvider = async (key) => {
    setTesting(key);
    try {
      const body = key === "findclix" && fc.findclix_api_key ? { api_key: fc.findclix_api_key, base_url: fc.findclix_base_url }
        : key === "openai" && oa.openai_api_key ? { api_key: oa.openai_api_key, base_url: oa.openai_base_url }
        : {};
      const r = await api.post(`/ai/providers/${key}/test`, body);
      r.data.success ? toast.success(`${key} OK (tested ${r.data.model_tested})`) : toast.error(r.data.error || "Test failed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setTesting(""); }
  };

  const patchModel = async (key, patch) => {
    try {
      await api.patch(`/ai/models/${key}`, patch);
      setModels((ms) => ms.map((m) => (m.key === key ? { ...m, ...patch } : m)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); load(); }
  };

  const deleteModel = async (key) => {
    if (!confirm(`Delete model ${key}?`)) return;
    try { await api.delete(`/ai/models/${key}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const savePremium = async () => {
    try { await api.patch("/settings", { ai_premium_fallback: premiumFallback }); toast.success("Premium fallback saved"); }
    catch (e) { toast.error("Save failed"); }
  };

  const PRESETS = {
    "Balanced Quality": { findclix_qwen3_30b: 48, findclix_gemma4_26b: 20, findclix_deepseek_v4_flash: 17, findclix_glm47_flash: 10, findclix_granite4_micro: 5 },
    "Maximum Quality": { findclix_deepseek_v4_flash: 40, findclix_gemma4_26b: 25, findclix_qwen3_30b: 25, findclix_glm47_flash: 10, findclix_granite4_micro: 0 },
    "Cost Saver": { findclix_qwen3_30b: 45, findclix_granite4_micro: 30, findclix_glm47_flash: 20, findclix_gemma4_26b: 5, findclix_deepseek_v4_flash: 0 },
    "Economy": { findclix_granite4_micro: 55, findclix_qwen3_30b: 30, findclix_glm47_flash: 15, findclix_gemma4_26b: 0, findclix_deepseek_v4_flash: 0 },
  };

  const applyPreset = async (name) => {
    try {
      await Promise.all(Object.entries(PRESETS[name]).map(([k, w]) => api.patch(`/ai/models/${k}`, { weight: w })));
      toast.success(`Applied "${name}" weights`);
      load();
    } catch (e) { toast.error("Failed to apply preset"); }
  };


  const addModel = async () => {
    if (!newModel.key || !newModel.model_id) { toast.error("key and model_id required"); return; }
    try {
      await api.post("/ai/models", newModel);
      toast.success("Model added");
      setNewModel({ key: "", provider: "findclix", model_id: "", display_name: "", role: "Custom" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };

  return (
    <div className="space-y-8">
      {/* Providers */}
      <Section title="AI Providers">
        <div className="grid grid-cols-2 gap-4" data-testid="ai-providers-grid">
          {providers.map((p) => (
            <div key={p.key} className="border border-[#2A2A2A] p-4 bg-[#0A0A0A]" data-testid={`provider-card-${p.key}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-sm text-white">{p.display_name}</span>
                {p.key_configured
                  ? <span className="text-[#00FF66] flex items-center gap-1 text-[10px] font-mono uppercase"><CheckCircle size={12} weight="fill" /> configured</span>
                  : <span className="text-[#FF3B30] flex items-center gap-1 text-[10px] font-mono uppercase"><Warning size={12} weight="fill" /> no key</span>}
              </div>
              <div className="text-[11px] text-[#71717A] font-mono space-y-0.5">
                <div>key: {p.key_masked || "—"}</div>
                <div>base: {p.base_url_effective}</div>
                <div>models: {p.enabled_model_count}/{p.model_count} enabled</div>
                {p.last_success_at && <div>last ok: {String(p.last_success_at).slice(0, 19)}</div>}
                {p.last_error && <div className="text-[#FF9500] truncate">err: {p.last_error}</div>}
              </div>
              <div className="mt-3">
                <BtnSecondary onClick={() => testProvider(p.key)} disabled={testing === p.key} testid={`provider-test-${p.key}`}>
                  {testing === p.key ? "Testing…" : "Test connection"}
                </BtnSecondary>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* DeepSeek Direct credentials */}
      <Section title="DeepSeek Direct credentials">
        <div className="grid grid-cols-3 gap-4">
          <label className="block">
            <span className="micro block mb-1.5">API key {ds.configured ? "· CONFIGURED" : "· NOT SET"}</span>
            <input type="password" placeholder="sk-…" value={ds.deepseek_api_key}
              onChange={(e) => setDs({ ...ds, deepseek_api_key: e.target.value })}
              data-testid="deepseek-key-input"
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white focus:border-[#E1FF00] focus:outline-none" />
          </label>
          <label className="block">
            <span className="micro block mb-1.5">Base URL</span>
            <input value={ds.deepseek_base_url}
              onChange={(e) => setDs({ ...ds, deepseek_base_url: e.target.value })}
              data-testid="deepseek-base-input"
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white focus:border-[#E1FF00] focus:outline-none" />
          </label>
          <label className="block">
            <span className="micro block mb-1.5">Default model</span>
            <input value={ds.deepseek_model}
              onChange={(e) => setDs({ ...ds, deepseek_model: e.target.value })}
              data-testid="deepseek-model-input"
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white focus:border-[#E1FF00] focus:outline-none" />
          </label>
        </div>
        <div className="mt-3 flex gap-2">
          <BtnPrimary onClick={saveDeepseek} testid="deepseek-save">Save DeepSeek</BtnPrimary>
          <BtnSecondary onClick={() => testProvider("deepseek_direct")} disabled={testing === "deepseek_direct"} testid="deepseek-test">
            {testing === "deepseek_direct" ? "Testing…" : "Test"}
          </BtnSecondary>
        </div>
      </Section>

      {/* FindClix credentials */}
      <Section title="FindClix AI credentials">
        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span className="micro block mb-1.5">API key {fc.configured ? "· CONFIGURED" : "· NOT SET"}</span>
            <input type="password" placeholder="ac_…" value={fc.findclix_api_key}
              onChange={(e) => setFc({ ...fc, findclix_api_key: e.target.value })}
              data-testid="findclix-key-input"
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white focus:border-[#E1FF00] focus:outline-none" />
          </label>
          <label className="block">
            <span className="micro block mb-1.5">Base URL</span>
            <input value={fc.findclix_base_url}
              onChange={(e) => setFc({ ...fc, findclix_base_url: e.target.value })}
              data-testid="findclix-base-input"
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white focus:border-[#E1FF00] focus:outline-none" />
          </label>
        </div>
        <label className="flex items-center gap-2 text-xs font-mono mt-3 cursor-pointer">
          <input type="checkbox" checked={fc.findclix_enabled}
            onChange={(e) => setFc({ ...fc, findclix_enabled: e.target.checked })}
            data-testid="findclix-enabled-toggle" className="accent-[#E1FF00]" />
          <span>FindClix provider enabled</span>
        </label>
        <div className="mt-3 flex gap-2">
          <BtnPrimary onClick={saveFindclix} testid="findclix-save">Save FindClix</BtnPrimary>
          <BtnSecondary onClick={() => testProvider("findclix")} disabled={testing === "findclix"} testid="findclix-test">
            {testing === "findclix" ? "Testing…" : "Test"}
          </BtnSecondary>
        </div>
      </Section>

      {/* OpenAI credentials */}
      <Section title="OpenAI credentials (your own key · GPT-5 family)">
        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span className="micro block mb-1.5">API key {oa.configured ? "· CONFIGURED" : "· NOT SET"}</span>
            <input type="password" placeholder="sk-…" value={oa.openai_api_key}
              onChange={(e) => setOa({ ...oa, openai_api_key: e.target.value })}
              data-testid="openai-key-input"
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white focus:border-[#E1FF00] focus:outline-none" />
          </label>
          <label className="block">
            <span className="micro block mb-1.5">Base URL</span>
            <input value={oa.openai_base_url}
              onChange={(e) => setOa({ ...oa, openai_base_url: e.target.value })}
              data-testid="openai-base-input"
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white focus:border-[#E1FF00] focus:outline-none" />
          </label>
        </div>
        <label className="flex items-center gap-2 text-xs font-mono mt-3 cursor-pointer">
          <input type="checkbox" checked={oa.openai_enabled}
            onChange={(e) => setOa({ ...oa, openai_enabled: e.target.checked })}
            data-testid="openai-enabled-toggle" className="accent-[#E1FF00]" />
          <span>OpenAI provider enabled</span>
        </label>
        <p className="text-[11px] text-[#71717A] font-mono mt-2">
          GPT-5 models: gpt-5-nano · gpt-5-mini · gpt-5.6-luna · gpt-5.6-terra · gpt-5.6-sol. Prompt caching is automatic; select them in Content Pool / Test Send.
        </p>
        <div className="mt-3 flex gap-2">
          <BtnPrimary onClick={saveOpenai} testid="openai-save">Save OpenAI</BtnPrimary>
          <BtnSecondary onClick={() => testProvider("openai")} disabled={testing === "openai"} testid="openai-test">
            {testing === "openai" ? "Testing…" : "Test"}
          </BtnSecondary>
        </div>
      </Section>

      {/* Cost summary */}
      {cost && (
        <Section title="AI Cost (usage accrues from go-live)">
          <div className="grid grid-cols-4 gap-3 mb-4">
            <Stat label="Spend today" v={money(cost.today.cost)} tid="cost-today" />
            <Stat label="Spend this month" v={money(cost.month.cost)} tid="cost-month" />
            <Stat label="Projected month" v={money(cost.month.projected_spend)} tid="cost-projected" />
            <Stat label="Avg $/content (mo)" v={money(cost.month.avg_cost_per_content)} tid="cost-per-content" />
          </div>
          <div className="border border-[#2A2A2A] bg-[#0A0A0A] overflow-x-auto">
            <table className="dtable w-full" data-testid="cost-by-model-table">
              <thead><tr><th>Model</th><th>API calls</th><th>Saved emails</th><th>Accept %</th><th>Cost</th><th>$ / saved</th><th>API ok</th><th>Latency</th></tr></thead>
              <tbody>
                {cost.by_model.length === 0 && <tr><td colSpan={8} className="text-center text-[#71717A] py-4 font-mono">[ no usage yet this month ]</td></tr>}
                {cost.by_model.map((m) => (
                  <tr key={m.model_key} data-testid={`waste-row-${m.model_key}`}>
                    <td className="font-mono text-xs">{m.display_name}</td>
                    <td className="font-mono text-xs">{m.requests}</td>
                    <td className="font-mono text-xs">{m.saved}</td>
                    <td className={`font-mono text-xs ${m.accept_rate < 25 ? "text-[#FF5F56]" : m.accept_rate < 50 ? "text-[#F4C430]" : "text-[#4ADE80]"}`} data-testid={`accept-rate-${m.model_key}`}>{m.accept_rate}%</td>
                    <td className="font-mono text-xs text-[#E1FF00]">{money(m.cost)}</td>
                    <td className="font-mono text-xs" title="Total spend on this model ÷ emails that passed every quality gate">{m.saved ? money(m.cost_per_saved) : "—"}</td>
                    <td className="font-mono text-xs">{m.success_rate}%</td>
                    <td className="font-mono text-xs">{m.avg_latency_ms}ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* AI error log — loaded on demand so it never adds load while a campaign runs */}
      <Section title="AI Error Log (content-generation API failures)">
        {!errLog ? (
          <div className="flex items-center gap-3">
            <p className="text-xs text-[#71717A] font-mono">Not loaded — click to fetch recent AI API errors on demand (kept off auto-load to stay light during live campaigns).</p>
            <button onClick={loadErrors} disabled={errLoading} data-testid="ai-err-load"
              className="ml-auto px-4 py-2 text-xs font-mono uppercase tracking-wider border border-[#E1FF00] text-[#E1FF00] hover:bg-[#E1FF00]/10 disabled:opacity-40 transition-colors whitespace-nowrap">
              {errLoading ? "Loading…" : "Load error log"}
            </button>
          </div>
        ) : (
        <>
          <div className="flex items-center gap-3 mb-4">
            <Stat label="Failed (24h)" v={String(errLog.total_failed_24h ?? 0)} tid="ai-err-24h" />
            <Stat label="Failed (all time)" v={String(errLog.total_failed_all ?? 0)} tid="ai-err-all" />
            <button onClick={loadErrors} disabled={errLoading} data-testid="ai-err-refresh"
              className="ml-auto px-3 py-2 text-xs font-mono uppercase tracking-wider border border-[#2A2A2A] text-[#A1A1AA] hover:border-[#E1FF00] hover:text-[#E1FF00] transition-colors">
              {errLoading ? "Loading…" : "Refresh"}
            </button>
          </div>
          {errLog.top_errors_24h?.length > 0 && (
            <div className="mb-4">
              <div className="micro mb-2">Top errors (24h)</div>
              <div className="space-y-1">
                {errLog.top_errors_24h.map((t, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs font-mono" data-testid={`ai-err-top-${i}`}>
                    <span className="text-[#EF4444] font-bold w-8">{t.count}×</span>
                    <span className="uppercase text-[#71717A] w-20">{t.provider}</span>
                    <span className="text-[#D4D4D8] truncate">{t.error}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="border border-[#2A2A2A] bg-[#0A0A0A] overflow-x-auto">
            <table className="dtable w-full" data-testid="ai-errors-table">
              <thead><tr><th>Time</th><th>Provider</th><th>Model</th><th>Source</th><th>Latency</th><th>Error</th></tr></thead>
              <tbody>
                {errLog.errors.length === 0 && <tr><td colSpan={6} className="text-center text-[#22C55E] py-4 font-mono">[ no AI generation errors — all clear ]</td></tr>}
                {errLog.errors.map((e, i) => (
                  <tr key={i} data-testid={`ai-error-row-${i}`}>
                    <td className="font-mono text-[10px] whitespace-nowrap">{e.ts ? new Date(e.ts).toLocaleString() : "—"}</td>
                    <td className="font-mono text-[10px] uppercase">{e.provider}</td>
                    <td className="font-mono text-[10px]">{e.model_display}</td>
                    <td className="font-mono text-[10px] text-[#71717A]">{e.source}</td>
                    <td className="font-mono text-[10px]">{e.latency_ms}ms</td>
                    <td className="font-mono text-[10px] text-[#EF4444] max-w-md truncate" title={e.error}>{e.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
        )}
      </Section>

      <Section title="AI Models (registry · editable)">
        <div className="flex justify-end mb-2">
          <BtnSecondary onClick={load} testid="ai-models-refresh">
            {loading ? "Refreshing…" : "Refresh"}
          </BtnSecondary>
        </div>
        <div className="border border-[#2A2A2A] bg-[#0A0A0A] overflow-x-auto mb-4">
          <table className="dtable w-full" data-testid="ai-models-table">
            <thead><tr>
              <th>On</th><th>Provider</th><th>Model</th><th>Role</th><th>Model ID</th>
              <th>In $/1M</th><th>Out $/1M</th><th>Weight</th><th>Think</th><th></th>
            </tr></thead>
            <tbody>
              {models.length === 0 && (
                <tr><td colSpan={10} className="text-center py-5 text-[#71717A] font-mono text-xs" data-testid="ai-models-empty">
                  {loading ? "Loading models…"
                    : modelsFailed ? "Couldn't load models — the server was busy. "
                    : "No models in the registry yet. "}
                  {!loading && modelsFailed && (
                    <button onClick={load} className="text-[#E1FF00] underline ml-1" data-testid="ai-models-retry">Retry</button>
                  )}
                </td></tr>
              )}
              {models.map((m) => (
                <tr key={m.key} data-testid={`model-row-${m.key}`}>
                  <td><input type="checkbox" checked={!!m.enabled} className="accent-[#E1FF00]"
                    onChange={(e) => patchModel(m.key, { enabled: e.target.checked })}
                    data-testid={`model-enabled-${m.key}`} /></td>
                  <td className="font-mono text-[10px] uppercase text-[#A1A1AA]">{m.provider === "findclix" ? "FindClix" : m.provider === "openai" ? "OpenAI" : "DeepSeek"}</td>
                  <td className="font-mono text-xs">{m.display_name}</td>
                  <td><input value={m.role || ""} onChange={(e) => setModels((ms) => ms.map((x) => x.key === m.key ? { ...x, role: e.target.value } : x))}
                    onBlur={(e) => patchModel(m.key, { role: e.target.value })}
                    className="w-24 bg-black border border-[#2A2A2A] px-1 py-0.5 text-[10px] text-white font-mono" /></td>
                  <td className="font-mono text-[10px] text-[#71717A]">{m.model_id}</td>
                  <td><NumCell v={m.input_price} step="0.001" tid={`model-input-price-${m.key}`} on={(v) => patchModel(m.key, { input_price: v })} /></td>
                  <td><NumCell v={m.output_price} step="0.001" tid={`model-output-price-${m.key}`} on={(v) => patchModel(m.key, { output_price: v })} /></td>
                  <td><NumCell v={m.weight} step="1" tid={`model-weight-${m.key}`} on={(v) => patchModel(m.key, { weight: v })} /></td>
                  <td><select value={m.thinking_enabled ? "on" : "off"} onChange={(e) => patchModel(m.key, { thinking_enabled: e.target.value === "on" })}
                    className="bg-black border border-[#2A2A2A] px-1 py-0.5 text-[10px] text-white font-mono" data-testid={`model-thinking-${m.key}`}>
                    <option value="off">off</option><option value="on">on</option></select></td>
                  <td>{!m.protected && (
                    <button onClick={() => deleteModel(m.key)} data-testid={`model-del-${m.key}`}
                      className="w-6 h-6 flex items-center justify-center border border-[#2A2A2A] hover:border-[#FF3B30] hover:text-[#FF3B30] text-[#A1A1AA]">
                      <Trash size={11} weight="bold" />
                    </button>)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid grid-cols-6 gap-2 items-end">
          <Field label="Key" v={newModel.key} on={(v) => setNewModel({ ...newModel, key: v })} tid="new-model-key" />
          <label className="block"><span className="micro block mb-1">Provider</span>
            <select value={newModel.provider} onChange={(e) => setNewModel({ ...newModel, provider: e.target.value })}
              data-testid="new-model-provider" className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-2 py-2 text-xs text-white font-mono">
              <option value="findclix">FindClix</option><option value="deepseek_direct">DeepSeek Direct</option><option value="openai">OpenAI</option></select></label>
          <Field label="Model ID" v={newModel.model_id} on={(v) => setNewModel({ ...newModel, model_id: v })} tid="new-model-id" />
          <Field label="Display name" v={newModel.display_name} on={(v) => setNewModel({ ...newModel, display_name: v })} tid="new-model-name" />
          <Field label="Role" v={newModel.role} on={(v) => setNewModel({ ...newModel, role: v })} tid="new-model-role" />
          <BtnPrimary onClick={addModel} testid="new-model-submit"><Plus size={12} weight="bold" /> Add model</BtnPrimary>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return <div className="border border-[#2A2A2A] p-6"><div className="micro mb-4">{title}</div>{children}</div>;
}
function Stat({ label, v, tid }) {
  return <div className="border border-[#2A2A2A] bg-[#0A0A0A] p-3" data-testid={tid}>
    <div className="micro mb-1">{label}</div><div className="font-mono text-lg text-[#E1FF00]">{v}</div></div>;
}
function NumCell({ v, on, step, tid }) {
  const [val, setVal] = useState(v);
  useEffect(() => { setVal(v); }, [v]);
  return <input type="number" step={step} value={val} data-testid={tid}
    onChange={(e) => setVal(e.target.value)}
    onBlur={(e) => on(Number(e.target.value))}
    className="w-16 bg-black border border-[#2A2A2A] px-1 py-0.5 text-[10px] text-white font-mono" />;
}
function Field({ label, v, on, tid }) {
  return <label className="block"><span className="micro block mb-1">{label}</span>
    <input value={v} onChange={(e) => on(e.target.value)} data-testid={tid}
      className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-2 py-2 text-xs text-white font-mono" /></label>;
}
